import * as React from 'react'
import WCC from 'wcc'
import { normalizeAndNotifyException } from '../../../lib/async-error-handler'
import * as Client from '../lib/shopify_client'
import StorefrontCartAPI from './cart/storefront_cart_api'

async function catchErrors<T>({
  fn,
  handleError,
}: {
  fn: () => Promise<T>,
  handleError: (e: Error) => void,
}): Promise<T | null> {
  try {
    return await fn()
  } catch (rawError) {
    const ex = normalizeAndNotifyException({ exception: rawError })
    handleError(ex)
    return null
  }
}

const ErrorDisplay = ({ error }: { error?: Error }) => (
  <>
    {error && (
      <div className="error">
        An error occurred loading your cart. Please try refreshing the page.
      </div>
    )}
  </>
)

const EmptyCart = ({ loading, error }: { loading: boolean, error: Error }) => (
  <div className="row">
    <ErrorDisplay error={error} />
    <div className="column medium-12 text-center">
      {loading ? 'Loading. Please wait...' : 'Empty Cart'}
    </div>
  </div>
)

interface IProps {
  cartID?: string
  b2bQuantityThreshold: number
  buildCheckoutAttributes: Client.IBuildCheckoutAttributes
  theme?: string
  ministryNames?: Record<string, string>
  updateItemQuantity: (args: {
    cartID: string,
    lineItemID: string,
    quantity: number,
  }) => Promise<Client.ICart>
}

const Cart: React.FunctionComponent<IProps> = ({
  cartID,
  b2bQuantityThreshold,
  buildCheckoutAttributes,
  theme,
  ministryNames,
}) => {
  const [cart, setCart] = React.useState<Client.ICart>(null)
  const [loading, setLoading] = React.useState<boolean>(!!cartID)
  const [error, setError] = React.useState<Error>()

  // Create the client with the correct API
  const shopifyClient = React.useMemo(() => {
    return new Client.ShopifyClient()
  }, [])

  React.useEffect(() => {
    async function loadCart() {
      if (!cartID) {
        return
      }
      const newCart = await catchErrors<Client.ICart>({
        fn: async () => shopifyClient.fetchCart(cartID),
        handleError: setError,
      })
      setCart(newCart)
      setLoading(false)
    }

    void loadCart()
  }, [cartID, shopifyClient])

  const handleUpdateItemQuantity = React.useCallback(
    async ({
      lineItemID,
      quantity,
    }: {
      lineItemID: string,
      quantity: number,
    }) => {
      setLoading(true)
      const newCart = await shopifyClient.updateItemQuantity({
        cartID,
        lineItemID,
        quantity,
      })
      setCart(newCart)
      setLoading(false)
    },
    [cartID, shopifyClient, setCart, setLoading],
  )

  const prepareCheckout = React.useCallback(async () => {
    const newCart = await shopifyClient.updateCart({
      cart: cart,
      attributes: {
        email: WCC.currentUser ? WCC.currentUser.email : null,
        shippingAddress: buildCheckoutAttributes.shippingAddress,
        b2bQuantityThreshold,
      },
    })

    setCart(newCart)
  }, [cart, shopifyClient, b2bQuantityThreshold, buildCheckoutAttributes])

  const handleRemoveItem = React.useCallback(
    async ({ lineItemID }: { lineItemID: string }) => {
      setLoading(true)
      const newCart = await shopifyClient.removeFromCart({
        cartID,
        lineItemIDS: [lineItemID],
      })
      setCart(newCart)
      setLoading(false)
    },
    [cartID, shopifyClient, setCart, setLoading],
  )
  if (cart && cart.lines.edges.length) {
    const items: Client.ICartLine[] = cart.lines.edges.map(edge => edge.node)
    const isPortal = theme === 'portal'
    return (
      <div className={`cart ${loading ? 'loading' : ''}`}>
        {isPortal && <h1 className="cart__heading">Shopping Cart</h1>}
        <ErrorDisplay error={error} />
        {items.map((item) => (
          <StorefrontCartAPI.LineItem
            key={item.id}
            item={item}
            updateItemQuantity={handleUpdateItemQuantity}
            removeItem={handleRemoveItem}
            theme={theme}
            ministryNames={ministryNames}
          />
        ))}
        <StorefrontCartAPI.CartSummary cart={cart} beforeCheckout={prepareCheckout} theme={theme} />
      </div>
    )
  } else {
    return <EmptyCart loading={loading} error={error} />
  }
}

export { Cart }
